/**
 * Match the backend URL policy before a form is sent. External links are
 * canonicalized to HTTPS; selected fields may keep same-site paths/fragments.
 */
export function normalizeUrl(value, options = {}) {
  const {
    allowRelative = false,
    fieldName = "URL",
    maxLength = 500,
  } = options;

  if (value == null || String(value).trim() === "") return "";
  if (typeof value !== "string") {
    throw new Error(`${fieldName} must be a URL string.`);
  }

  let candidate = value.trim();
  if (candidate.length > maxLength) {
    throw new Error(`${fieldName} must be ${maxLength} characters or fewer.`);
  }
  if (/\s|\\/.test(candidate)) {
    throw new Error(`${fieldName} must not contain spaces or backslashes.`);
  }
  if (/%(?![0-9a-f]{2})/i.test(candidate)) {
    throw new Error(`${fieldName} contains an invalid percent escape.`);
  }

  if (allowRelative && (candidate.startsWith("/") || candidate.startsWith("#"))) {
    if (candidate.startsWith("//")) {
      throw new Error(
        `${fieldName} must use a full https:// URL or a single-slash site path.`
      );
    }
    if (candidate === "#") {
      throw new Error(`${fieldName} must include a path or anchor after its prefix.`);
    }
    return candidate;
  }

  if (/^[a-z][a-z0-9+.-]*:/i.test(candidate) && !/^https?:\/\//i.test(candidate)) {
    throw new Error(`${fieldName} must use http:// or https://.`);
  }

  while (/^https?:\/\//i.test(candidate)) {
    candidate = candidate.replace(/^https?:\/\//i, "");
  }

  const normalized = `https://${candidate}`;
  if (normalized.length > maxLength) {
    throw new Error(`${fieldName} must be ${maxLength} characters or fewer.`);
  }

  let parsed;
  try {
    parsed = new URL(normalized);
  } catch {
    throw new Error(`${fieldName} is not a valid web URL.`);
  }

  const labelsAreValid = parsed.hostname
    .replace(/^\[|\]$/g, "")
    .split(".")
    .every((label) =>
      parsed.hostname.includes(":")
        ? /^[0-9a-f:]+$/i.test(parsed.hostname.replace(/^\[|\]$/g, ""))
        : /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i.test(label)
    );
  if (
    parsed.protocol !== "https:" ||
    !parsed.hostname ||
    !labelsAreValid ||
    parsed.username ||
    parsed.password
  ) {
    throw new Error(`${fieldName} is not a valid web URL.`);
  }

  return normalized;
}
